import {useEffect, useState} from 'react';

import documentsBackend from '@lexian/api/documents-api';
import notificationsAsyncBackend, {
  DocumentPreviewPdfCreatedPushNotification,
} from '@lexian/api/notifications-async-backend';

export const useRefreshableDocumentPdfUrl = ({
  casusId,
  documentId,
  isDeleted = false,
  highlightTexts,
}: {
  casusId: string;
  documentId: string;
  isDeleted?: boolean;
  highlightTexts?: string[];
}): string => {
  const documentUrl = isDeleted
    ? documentsBackend.downloadDeletedDocumentPdfURL({
        casusId,
        deletedDocumentId: documentId,
      })
    : documentsBackend.downloadPdfURL({
        casusId,
        documentId,
        highlightTexts,
      });

  const [refreshableUrl, setRefreshableUrl] = useState(documentUrl);
  useEffect(() => {
    setRefreshableUrl(documentUrl);
  }, [documentUrl]);

  useEffect(() => {
    let unsubscribeToPdfChanges: () => void | void;
    notificationsAsyncBackend.initializeOnConnect(() => {
      unsubscribeToPdfChanges = notificationsAsyncBackend.topicCasusSpecific(
        {casusId},
        (notification) => {
          if (
            notification.body.pushNotificationType ===
            'DocumentPreviewPdfCreatedPushNotification'
          ) {
            const body =
              notification.body as DocumentPreviewPdfCreatedPushNotification;
            if (body.casusId === casusId && documentId === body.documentId) {
              const separator = documentUrl.includes('?') ? '&' : '?';
              setRefreshableUrl(
                `${documentUrl}${separator}refreshedAt=${Date.now()}`
              );
            }
          }
        }
      ).unsubscribe;
    });
    return () => unsubscribeToPdfChanges?.();
  }, [casusId, documentUrl, documentId]);

  return refreshableUrl;
};

export function useTemporaryDocumentPdfUrl(
  temporaryDocumentId: string
): string {
  return documentsBackend.downloadTemporaryDocumentPdfURL({
    temporaryDocumentId,
  });
}
